import styled, { css } from 'styled-components';
import hexToRgba from 'hex-rgba';
import { getStyle as getCheckBoxStyles } from '@ids/ddms-styled/comps/checkbox/intuit/light';

const blue02 = '#0077c5';
// Standard Intuit Themes
const theme = {
  intuit: {
    color: blue02,
    bordercolor: getCheckBoxStyles('field.borderColor')
  },
  quickbooks: {
    color: '#2CA01C'
  },
  mint: {
    color: '#00c1bf'
  },
  proconnect: {
    color: blue02
  },
  turbotax: {
    color: '#05a4b5'
  }
};

// These are common styles that are used both in Radio and Checkbox Inner Span.
// Inner Error Style
// Warning Style
// a11yFocus
const commonStyles = css`
  ${props =>
    props.validationType === 'error' &&
    props.validationMessage !== '' &&
    `
    border-style: solid
    border-color: #ff6a00;
    background-color: rgba(213,43,30,.1)
  `};

  ${props =>
    props.validationType === 'warning' &&
    props.validationMessage !== '' &&
    `
    border-style: solid
    border-color: #d52b1e;
    background-color: rgba(255,187,0,.1);
  `};

  ${props =>
    props.a11yFocus &&
    `
    box-shadow: ${
      props.theme === 'quickbooks'
        ? `0 0 0 3px ${blue02}`
        : `0 0 0 2px ${hexToRgba(theme[props.theme].color, 25)}`
    }`};
`;

// Base Styles
// Checkbox Container Disabled Styles
// Radio Container when Checked
export const Container = styled.span`
  top: -1px;
  outline: none;
  display: inline-block;
  line-height: 1;
  position: relative;
  /* vertical-align: text-bottom; */
  float: left;
  height: 20px;
  width: 20px;

  ${props => props.disabled && `cursor: not-allowed;`};

  ${props =>
    props.checked &&
    `
    display: inline-block;
    position: relative;
    cursor: pointer;
  `};
`;

// Checkbox Input Styles
// Checkbox Input + Checked Styles
// Checkbox Input + Disabled Styles
export const CheckboxInput = styled.input`
  position: absolute;
  left: 0;
  cursor: pointer;
  opacity: 0;
  top: 0;
  bottom: 0;
  right: 0;
  margin: 0;
  height: 100%;
  width: 100%;

  ${props =>
    props.checked &&
    `
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 2px;
      content: '';
      visibility: hidden;
    };
  `};

  ${props =>
    props.disabled &&
    `
    cursor: not-allowed;
  `};
`;

// Radio Input Styles
// Radio Input disabled styles
export const RadioInput = styled.input`
  position: absolute;
  left: 0;
  z-index: 1;
  cursor: pointer;
  opacity: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  margin: 3px;

  ${props =>
    props.disabled &&
    `
    cursor: not-allowed;
  `};
`;

// Styles for Checkbox Inner Span
export const InnerCheckboxSpan = styled.span`
  box-sizing: border-box;
  position: relative;
  top: 0;
  left: 0;
  display: block;
  width: 20px;
  height: 20px;
  border: 1px solid ${theme.intuit.bordercolor};
  border-radius: 2px;
  background-color: #fff;
  transition: all 0.3s;

  &:after {
    box-sizing: border-box;
    transform: rotate(45deg) scale(0);
    position: absolute;
    left: 4px;
    top: 1px;
    display: table;
    width: 5px;
    height: 8px;
    border: 2px solid #fff;
    border-top: 0;
    border-left: 0;
    content: '';
    transition: all 0.1s cubic-bezier(0.71, -0.46, 0.88, 0.6);
  }

  ${props =>
    props.checked &&
    `
    background-color: ${theme[props.theme].color};
    border-color: ${theme[props.theme].color};

    ${props.theme === 'quickbooks' &&
      `
      background-color: #fff;
    `};

    &:after {
      transform: rotate(45deg) scale(1);
      position: absolute;
      left: 6px;
      top: 2.5px;
      display: table;
      width: 7px;
      height: 11px;
      border: 2.5px solid;
      border-color: ${
        props.theme === 'quickbooks' ? theme.quickbooks.color : '#fff'
      };
      border-top: 0;
      border-left: 0;
      content: "";
      transition: all .2s cubic-bezier(.12,.4,.29,1.46);
    };
  `};

  ${props =>
    props.indeterminate &&
    `
  background-color: ${theme[props.theme].color};
  border-color: ${theme[props.theme].color};

  ${props.theme === 'quickbooks' &&
    `
    background-color: #fff;
  `};

  &:after {
    transform: scale(1);
    left: 4px;
    top: 8px;
    width: 10px;
    height: 1px;
    border: 2px solid;
    border-top: 0;
    border-left: 0;
    border-color: ${
      props.theme === 'quickbooks' ? theme.quickbooks.color : '#fff'
    };
  };
`};

  ${props =>
    props.disabled &&
    `
    background-color: transparent;
    border-color: ${
      props.theme === 'quickbooks'
        ? hexToRgba(theme.intuit.bordercolor, 40)
        : hexToRgba(theme.intuit.bordercolor, 50)
    };
    cursor: not-allowed;
  `};

  ${props =>
    props.disabled &&
    props.checked &&
    `
    &:after {
      transform: rotate(45deg) scale(1);
      position: absolute;
      left: 6px;
      top: 1px;
      display: table;
      width: 6px;
      height: 11px;
      border: 2px solid ${theme.intuit.bordercolor};
      border-top: 0;
      border-left: 0;
      content: "";
      transition: all .2s cubic-bezier(.12,.4,.29,1.46)
    };
`};
  ${commonStyles}
`;

// Radio Inner Styles
// Radio Inner Checked Styles
// Radio Inner Disabled
export const InnerRadioSpan = styled.span`
  ${props =>
    props &&
    `
    box-sizing: border-box;
    position: relative;
    top: 0px;
    left: 0;
    display: block;
    width: 20px;
    height: 20px;
    border-radius: 20px;
    border: 1px solid ${theme.intuit.bordercolor};
    background-color: #fff;
    transition: all .3s;

    &:after {
      background-color: ${blue02};
      position: absolute;
      width: 12px;
      height: 12px;
      left: 3px;
      top: 3px;
      border-radius: 6px;
      display: table;
      border-top: 0;
      border-left: 0;
      content: "";


      opacity: 0;
      transform: scale(0);
      transition: all .3s cubic-bezier(.78,.14,.15,.86);
      background-color: ${theme[props.theme].color};
    }
  `};

  ${props =>
    props.checked &&
    `
      border-color: ${theme[props.theme].color};
      border: 1px solid ${theme[props.theme].color};
      &:after {
        transform: scale(1);
        opacity: 1;
        transition: all 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
      }
    `};

  ${props =>
    props.disabled &&
    `
      background-color: transparent;
      border-color: rgba(186,190,197,.5);
      cursor: not-allowed;
    `};

  ${commonStyles};
`;
